import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import {
  concatMap,
  distinctUntilChanged,
  filter,
  from,
  map,
  mergeMap,
  Observable,
  of,
  shareReplay,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  timer,
  tap,
} from 'rxjs';
import { CurrentUser } from '@core/auth/current-user';
import { StyleManager } from '@core/services/style-manager.service';
import { Event as RouterEvent, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { MediaMatcher } from '@angular/cdk/layout';
import { AppService } from '@app/app.service';
import { TranslateConfigService } from '@core/translate-config.service';
import { AuthService } from '@core/auth/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { LanguageModalComponent } from '@shared/language-modal/language-modal.component';
import { environment } from '@environments/environment';
import { BannerType, BannerSize } from '@shared/components/banner/banner.model';
import { BannerService } from '@shared/services/banner.service';
import { GlobalConfigurationApi } from '@api/services/global-configuration-api';
import { DateTime } from 'luxon';
import { BannerSettings, BannerExtended } from '@app/admin/settings/settings.model';
import { BannerComponent } from '@shared/components/banner/banner.component';
import { UserSurveyApi } from '@api/services/user-survey-api';
import { UserSurveyResponseStatus } from '@api/models/user-survey-response-status';
import { SurveyDialogComponent } from '@shared/survey-dialog/survey-dialog.component';
import { UserRoleEnum } from '@app/admin/user/model/user.model';
import { JUST_LOGGED_IN_KEY } from 'src/app/constants';

const TALLY_SURVEY_URL_PATTERN = /tally\.so\/r\/([a-zA-Z0-9]+)/;

@Component({
  selector: 'flow-private-layot',
  templateUrl: './private-layot.component.html',
  styleUrls: ['./private-layot.component.scss'],
})
export class PrivateLayotComponent implements OnInit, OnDestroy {
  @ViewChild('top') top: ElementRef;
  @ViewChild('bannerContainer', { read: ViewContainerRef, static: true }) bannerContainer!: ViewContainerRef;

  destroy$ = new Subject<void>();
  routesWithoutDashboard = ['/login', '/register', '/confirm-email', '/choose-tenant'];
  currentLanguage: string = '';
  currentRoute: string;
  private loggedInSubscription: Subscription;
  public isLoggedIn: boolean;
  private _modeOverQueryListener: (event: MediaQueryListEvent) => void;
  showNavigateToTopBtn = false;
  showNavSettings = false;
  currentUser: CurrentUser;
  initials = '';
  BannerType = BannerType;
  BannerSize = BannerSize;
  banners: BannerComponent[] = [];
  bannersLength: number = 0;
  bannersHeight: number = 0;

  constructor(
    private styleManager: StyleManager,
    public router: Router,
    public changeDetectorRef: ChangeDetectorRef,
    public media: MediaMatcher,
    public appService: AppService,
    private transalteConfigService: TranslateConfigService,
    public authService: AuthService,
    public dialog: MatDialog,
    private bannerService: BannerService,
    private globalConfiguration: GlobalConfigurationApi,
    private userSurveyApi: UserSurveyApi,
  ) {
    this.isLoggedIn = authService.isLoggedIn();
    this.currentRoute = this.router.url;

    router.events.subscribe((event: RouterEvent) => {
      this.navigationInterceptor(event);
    });

    this.appService.modeOverQuery = media.matchMedia('(max-width: 1279px)');

    this._modeOverQueryListener = (event: MediaQueryListEvent) => {
      this.appService.navClosed = event.matches;
      changeDetectorRef.detectChanges();
    };

    this.appService.modeOverQuery.addEventListener('change', this._modeOverQueryListener);
    this.appService.isDark = this.styleManager.isDark;
    this.currentLanguage = this.transalteConfigService.currentLanguage;
  }

  // @HostListener('window:scroll', ['$event'])
  // onScroll() {
  //   const documentHeight = document.documentElement.scrollHeight;
  //   const scrollTop = window.scrollY || document.documentElement.scrollTop;
  //   const scrollPercentage = (scrollTop / (documentHeight - window.innerHeight)) * 100;

  //   // Show button when scrolled past 50% of the page
  //   this.showNavigateToTopBtn = scrollPercentage >= 50;
  // }

  ngOnInit(): void {
    if (!this.isLoggedIn)
      this.authService.user$.subscribe(user => {
        this.currentUser = user;
      });

    this.currentUser = this.authService.getUser();

    this.appService.navClosed = this.appService.modeOverQuery.matches;
    this.loggedInSubscription = this.authService.isLoggedIn$.pipe(distinctUntilChanged()).subscribe(loggedIn => {
      this.isLoggedIn = loggedIn;
      this.currentUser = this.authService.getUser();
      this.currentLanguage = this.transalteConfigService.currentLanguage;
    });

    this.getBannerConfig();
    this.checkSurveyStatus();
  }

  checkSurveyStatus(): void {
    // Only set on an actual login (AuthService.setLoggedInState), so a refresh skips this.
    if (!sessionStorage.getItem(JUST_LOGGED_IN_KEY)) {
      return;
    }
    sessionStorage.removeItem(JUST_LOGGED_IN_KEY);

    if (this.currentUser?.roleId !== UserRoleEnum.Customer || !this.currentUser?.id) {
      return;
    }

    const userId = this.currentUser.id;

    this.userSurveyApi
      .getUserSurveyUrl()
      .pipe(
        map(surveyUrl => this.extractSurveyFormId(surveyUrl)),
        filter((formId): formId is string => !!formId),
        switchMap(formId =>
          this.userSurveyApi.getUserSurveyStatus(userId).pipe(map(response => ({ formId, response })))
        ),
        filter(({ response }) => response?.status !== UserSurveyResponseStatus.Completed),
        map(({ formId }) => formId),
        takeUntil(this.destroy$)
      )
      .subscribe(formId => {
        this.dialog.open(SurveyDialogComponent, {
          width: '700px',
          maxHeight: '80vh',
          data: {
            formId,
            userId,
            customerId: this.currentUser?.currentCustomerId ?? this.currentUser?.customerId,
          },
        });
      });
  }

  private extractSurveyFormId(surveyUrl?: string): string | null {
    return surveyUrl?.match(TALLY_SURVEY_URL_PATTERN)?.[1] ?? null;
  }

  calculateBannersHeight() {
    this.bannerService.banners$.pipe(
      filter(banners => banners.length > 0),
      takeUntil(this.destroy$)
    ).subscribe(banners => {
      this.banners = banners;
      const bannerSize = banners.find(b => b.size === BannerSize.MD) ? 36 : 28;
      this.bannersLength = banners.length;
      this.bannersHeight = this.bannersLength * bannerSize;
    });
  }

  toggleDarkTheme() {
    this.styleManager.toggleDarkTheme();
    this.appService.isDark = !this.appService.isDark;
  }

  changeLanguage(lang: string) {
    this.transalteConfigService.changeLanguage(lang);
    this.currentLanguage = lang;
  }

  openLanguageModal(): void {
    this.dialog.open(LanguageModalComponent, {
      width: '1000px',
      height: '70vh',
      data: {
        changeLanguage: (lang: string) => this.changeLanguage(lang),
      },
    });
  }

  navigateToWiki() {
    window.open(environment.wikiUrl, '_blank').focus();
  }

  scrollToTop() {
    this.top.nativeElement.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  navigationInterceptor(event: RouterEvent): void {
    const hasToolbarFormActions = this.appService.hasToolbarFormActions;
    if (event instanceof NavigationStart) {
      this.appService.hasToolbarFormActions = false;
      this.appService.navHidden = this.routesWithoutDashboard.some(route => event.url.includes(route));
    }
    if (event instanceof NavigationEnd) {
      // on navigation end
      this.showNavigateToTopBtn = false;
    }
    if (event instanceof NavigationCancel || event instanceof NavigationError) {
      this.appService.hasToolbarFormActions = hasToolbarFormActions;
    }
  }

  getBannerConfig() {
    const config$ = this.globalConfiguration.getBannerConfig().pipe(
      map(config => this.mapBannerSettings(config)),
      shareReplay(1)
    );

    const loadedBanners$ = this.initBanner(config$);
    this.destroyBanner(loadedBanners$);
  }

  calcTimerDelay(time?: string): number {
    const startTime = DateTime.fromISO(time).toLocal();
    const now = DateTime.local();
    const durationUntilStart = startTime.diff(now);
    const delayMs = durationUntilStart.as('milliseconds');

    if (delayMs < 0 || isNaN(delayMs)) {
      return 0;
    }

    if (isNaN(delayMs)) {
      return null;
    }

    return delayMs;
  }

  isBannerExpired(cfg: BannerExtended): boolean {
    if (!cfg.endDate) {
      return false;
    }

    const endTime = DateTime.fromISO(cfg.endDate).toLocal();
    const now = DateTime.local();

    return !endTime.isValid || endTime <= now;
  }

  initBanner(config$: Observable<BannerExtended[]>): Observable<BannerExtended> {
    return config$.pipe(
      switchMap(configs => from(configs)),
      filter(cfg => !!cfg.isEnabled),
      concatMap(cfg => {
        if (this.isBannerExpired(cfg)) {
          return of(null);
        }

        const delay = this.calcTimerDelay(cfg.startDate);
        const time$ = delay !== null ? timer(delay).pipe(map(() => cfg)) : of(null);
        return time$;
      }),
      filter((cfg): cfg is BannerExtended => !!cfg),
      tap(cfg => {
        this.bannerService.loadComponent(this.bannerContainer, cfg);
      }),
      tap(() => {
        this.calculateBannersHeight();
      }),
      takeUntil(this.destroy$),
      shareReplay(1)
    );
  }

  destroyBanner(loadedBanners$: Observable<BannerExtended>) {
    loadedBanners$
      .pipe(
        filter(cfg => !!cfg.endDate),
        mergeMap(cfg => {
          const delay = this.calcTimerDelay(cfg.endDate);
          const time$ = timer(delay).pipe(map(() => cfg));
          return time$;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(cfg => {
        this.bannerService.destroyComponent(cfg);
      });
  }

  private mapBannerSettings(config: BannerSettings): BannerExtended[] {
    const banners: BannerExtended[] = [];
    const disasterBanner = config?.disasterBanner;
    const informationBanner = config?.informationBanner;

    if (disasterBanner?.isEnabled) {
      banners.push({
        id: 0,
        size: BannerSize.MD,
        type: BannerType.Disaster,
        canClose: disasterBanner.canClose ?? false,
        isEnabled: disasterBanner.isEnabled,
        startDate: disasterBanner.startDate,
        endDate: disasterBanner.endDate,
        text: disasterBanner.text,
      });
    }

    if (informationBanner?.isEnabled) {
      banners.push({
        id: 1,
        size: BannerSize.MD,
        type: BannerType.Success,
        canClose: informationBanner.canClose ?? false,
        isEnabled: informationBanner.isEnabled,
        startDate: informationBanner.startDate,
        endDate: informationBanner.endDate,
        text: informationBanner.text,
      });
    }

    return banners;
  }

  ngOnDestroy(): void {
    this.appService.modeOverQuery.removeEventListener('change', this._modeOverQueryListener);
    this.loggedInSubscription.unsubscribe();
    this.bannerService.clearBanners();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
